import { useEffect, useState, type FormEvent } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { useLocation } from 'wouter';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserCircle, KeyRound } from 'lucide-react';
import { api, ApiCallError } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { useCurrentAdmin } from '@/hooks/use-current-admin';
import { AdminShell } from '@/components/admin-shell';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { FieldError } from '@/components/ui/field-error';

export function AdminProfilePage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading } = useCurrentAdmin();

  if (isLoading) return <PageLoader />;
  if (!me) {
    navigate('/login');
    return null;
  }

  return (
    <AdminShell>
      <div>
        <h1 className="text-2xl font-bold">Mi perfil</h1>
        <p className="text-sm text-muted-foreground">
          Tus datos como usuario del panel admin.
        </p>
      </div>

      <ProfileCard me={me} />
      <PasswordCard />
    </AdminShell>
  );
}

interface AdminMe {
  id: string;
  email: string;
  fullName: string;
  role: 'accountant' | 'superadmin';
}

function ProfileCard({ me }: { me: AdminMe }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ fullName: me.fullName, email: me.email });
  const [issues, setIssues] = useState<Record<string, string[]>>({});

  useEffect(() => {
    setForm({ fullName: me.fullName, email: me.email });
  }, [me.fullName, me.email]);

  const mutation = useMutation({
    mutationFn: () =>
      api('/api/auth/me', {
        method: 'PATCH',
        body: JSON.stringify({
          fullName: form.fullName.trim(),
          email: form.email.trim().toLowerCase(),
        }),
      }),
    onSuccess: () => {
      toast.success('Perfil actualizado');
      qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (err) => {
      if (err instanceof ApiCallError) {
        if (err.status === 409 && err.body.error === 'email_already_exists') {
          setIssues({ email: ['Ese email ya está usado por otro admin'] });
          return;
        }
        if (err.status === 400) {
          const body = err.body as { issues?: Record<string, string[]> };
          if (body.issues) {
            setIssues(body.issues);
            return;
          }
        }
      }
      toast.error('No se pudieron guardar los cambios');
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setIssues({});
    mutation.mutate();
  }

  const dirty = form.fullName !== me.fullName || form.email !== me.email;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <UserCircle className="size-4" />
          Datos personales
        </CardTitle>
        <CardDescription>Tu nombre y email que ven los demás admins en el panel.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4 max-w-md">
          <div className="space-y-1.5">
            <Label htmlFor="fullName" required>
              Nombre completo
            </Label>
            <Input
              id="fullName"
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            />
            <FieldError error={issues.fullName} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email" required>
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <FieldError error={issues.email} />
          </div>

          <div className="space-y-1.5">
            <Label>Rol</Label>
            <div>
              <Badge variant={me.role === 'superadmin' ? 'default' : 'secondary'}>
                {me.role === 'superadmin' ? 'Superadmin' : 'Contador'}
              </Badge>
              <p className="text-xs text-muted-foreground mt-1.5">
                Solo un superadmin puede cambiar el rol de un usuario.
              </p>
            </div>
          </div>

          <Button type="submit" disabled={!dirty || mutation.isPending}>
            {mutation.isPending ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function PasswordCard() {
  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirm: '',
  });
  const [issues, setIssues] = useState<Record<string, string[]>>({});

  const mutation = useMutation({
    mutationFn: () =>
      api('/api/auth/password', {
        method: 'POST',
        body: JSON.stringify({
          currentPassword: form.currentPassword,
          newPassword: form.newPassword,
        }),
      }),
    onSuccess: () => {
      toast.success('Contraseña actualizada');
      setForm({ currentPassword: '', newPassword: '', confirm: '' });
    },
    onError: (err) => {
      if (err instanceof ApiCallError) {
        if (err.status === 400 && err.body.error === 'invalid_current_password') {
          setIssues({ currentPassword: ['La contraseña actual no es correcta'] });
          return;
        }
        if (err.status === 400) {
          const body = err.body as { issues?: Record<string, string[]> };
          if (body.issues) {
            setIssues(body.issues);
            return;
          }
        }
      }
      toast.error('No se pudo cambiar la contraseña');
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setIssues({});
    if (form.newPassword !== form.confirm) {
      setIssues({ confirm: ['Las contraseñas no coinciden'] });
      return;
    }
    if (form.newPassword.length < 8) {
      setIssues({ newPassword: ['Mínimo 8 caracteres'] });
      return;
    }
    mutation.mutate();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <KeyRound className="size-4" />
          Cambiar contraseña
        </CardTitle>
        <CardDescription>
          Por seguridad, tenés que confirmar tu contraseña actual antes de cambiarla.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4 max-w-md">
          <div className="space-y-1.5">
            <Label htmlFor="currentPassword" required>
              Contraseña actual
            </Label>
            <Input
              id="currentPassword"
              type="password"
              autoComplete="current-password"
              value={form.currentPassword}
              onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
            />
            <FieldError error={issues.currentPassword} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="newPassword" required>
              Nueva contraseña
            </Label>
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              value={form.newPassword}
              onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
            />
            <FieldError error={issues.newPassword} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirm" required>
              Repetí la nueva
            </Label>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            />
            <FieldError error={issues.confirm} />
          </div>

          <Button
            type="submit"
            disabled={
              mutation.isPending ||
              !form.currentPassword ||
              !form.newPassword ||
              !form.confirm
            }
          >
            {mutation.isPending ? 'Cambiando…' : 'Cambiar contraseña'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
